import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  Fade,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
  DialogTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isAndroid, isIOS } from "src/utils/platformUtils";
import { WidgetContext } from "src/contexts/WidgetContext";
import { extractKeeperData } from "src/utils/keeperNotes.utils";

export interface KeeperFormData {
  numberOfKeepers: string;
  isUnlimited: boolean;
  requiresForfeiture: boolean;
  forfeitOriginalRound: boolean;
  escalatingCost: boolean;
  roundCostPerSeason: string;
  otherKeeperLogic: string;
}

interface KeeperDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: KeeperFormData) => void;
  rawNotes?: string;
  isSubmitting?: boolean;
  /** From commonLeagueData.league_info.league_type — drives Unlimited (Dynasty) checkbox */
  leagueType?: string;
}

const ORANGE = PureLightThemeCaaS.colors.alpha.orange[100];
const INACTIVE_TOGGLE_BG = "#4A4A4A";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "6px",
    fontFamily: '"Inter", sans-serif',
    fontSize: "14px",
    "& fieldset": {
      borderColor: "#D0D0D0",
    },
    "&:hover fieldset": {
      borderColor: "#B0B0B0",
    },
    "&.Mui-focused fieldset": {
      borderColor: ORANGE,
      borderWidth: "1px",
    },
    "&.Mui-disabled": {
      backgroundColor: PureLightThemeCaaS.colors.alpha.gray[30],
      opacity: 1, // reset MUI
      "& fieldset": { borderColor: PureLightThemeCaaS.colors.alpha.gray[30] },
    },
  },
  "& .MuiInputBase-input": {
    color: PureLightThemeCaaS.colors.alpha.black[100],
    fontFamily: '"Inter", sans-serif',
    "&::placeholder": {
      color: "#999",
      opacity: 1,
    },

  },
};

interface YesNoToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
}

const YesNoToggle: React.FC<YesNoToggleProps> = ({ value, onChange }) => (
  <Box
    sx={{
      position: "relative",
      display: "flex",
      flexShrink: 0,
      borderRadius: "20px",
      height: 25,
      minWidth: 70,
      backgroundColor: INACTIVE_TOGGLE_BG,
    }}
  >
    {/* Sliding thumb — wider than half so it overlaps past center (switch look) */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        width: "58%",
        borderRadius: "20px",
        backgroundColor: ORANGE,
        transition: "left 200ms ease, right 200ms ease",
        left: value ? "auto" : 0,
        right: value ? 0 : "auto",
        pointerEvents: "none",
      }}
    />
    {(["No", "Yes"] as const).map((label) => {
      const isYes = label === "Yes";
      return (
        <Box
          key={label}
          component="button"
          type="button"
          onClick={() => onChange(isYes)}
          sx={{
            flex: 1,
            position: "relative",
            zIndex: 1,
            border: "none",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "bold",
            fontFamily: '"Inter", sans-serif',
            color: "#fff",
            backgroundColor: "transparent",
            px: 1.5,
          }}
        >
          {label}
        </Box>
      );
    })}
  </Box>
);

interface ToggleRowProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ label, value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 0,
      py: 1,
      px: 0.5,
      borderBottom: "0.5px solid #000000"
    }}
  >
    <Typography variant="body2" color="#000000" sx={{ lineHeight: 1.3, flex: 1 }}>
      {label}
    </Typography>
    <YesNoToggle value={value} onChange={onChange} />
  </Box>
);

const KeeperDrawer: React.FC<KeeperDrawerProps> = ({ open, onClose, onSubmit, rawNotes = "", isSubmitting = false, leagueType }) => {
  const [numberOfKeepers, setNumberOfKeepers] = useState("");
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [requiresForfeiture, setRequiresForfeiture] = useState(false);
  const [forfeitOriginalRound, setForfeitOriginalRound] = useState(false);
  const [escalatingCost, setEscalatingCost] = useState(false);
  const [roundCostPerSeason, setRoundCostPerSeason] = useState("");
  const [otherKeeperLogic, setOtherKeeperLogic] = useState("");

  const { aiSettings } = useContext(WidgetContext);

  // Repopulate form from embedded keeper block in notes when drawer opens.
  // Always reset: if this league has no keeper block, clear stale values from a previous league.
  // Unlimited (Dynasty) checkbox follows common league_type: dynasty → checked; keeper/redraft/other → unchecked.
  useEffect(() => {
    if (!open) return;
    const { keeperData } = extractKeeperData(rawNotes);
    const normalizedLeagueType = typeof leagueType === "string" ? leagueType.trim().toLowerCase() : "";

    setNumberOfKeepers(keeperData?.numberOfKeepers || "");
    setIsUnlimited(normalizedLeagueType === "dynasty");
    setRequiresForfeiture(keeperData?.requiresForfeiture || false);
    setForfeitOriginalRound(keeperData?.forfeitOriginalRound || false);
    setEscalatingCost(keeperData?.escalatingCost || false);
    setRoundCostPerSeason(keeperData?.roundCostPerSeason || "");
    setOtherKeeperLogic(keeperData?.otherKeeperLogic || "");
  }, [open, rawNotes, leagueType]);

  // Check if the form is valid to submit
  const canSubmit = useMemo(() => {
    const hasKeeperCount = isUnlimited || numberOfKeepers.trim() !== "";
    if (!hasKeeperCount) return false;

    if (requiresForfeiture && escalatingCost) {
      return roundCostPerSeason.trim() !== "" && /^\d+$/.test(roundCostPerSeason);
    }
    return true;
  }, [isUnlimited, numberOfKeepers, requiresForfeiture, escalatingCost, roundCostPerSeason]);

  const handleNumericChange = (value: string, setter: (v: string) => void) => {
    if (value === "" || /^\d+$/.test(value)) {
      setter(value);
    }
  };

  const handleRequiresForfeitureChange = (value: boolean) => {
    setRequiresForfeiture(value);
    if (!value) {
      setForfeitOriginalRound(false);
      setEscalatingCost(false);
      setRoundCostPerSeason("");
    }
  };

  const handleEscalatingCostChange = (value: boolean) => {
    setEscalatingCost(value);
    if (!value) {
      setRoundCostPerSeason("");
    }
  };

  const handleUnlimitedChange = (checked: boolean) => {
    setIsUnlimited(checked);
    if (checked) {
      setNumberOfKeepers("");
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const data: KeeperFormData = {
      numberOfKeepers,
      isUnlimited,
      requiresForfeiture,
      forfeitOriginalRound: requiresForfeiture ? forfeitOriginalRound : false,
      escalatingCost: requiresForfeiture ? escalatingCost : false,
      roundCostPerSeason: requiresForfeiture && escalatingCost ? roundCostPerSeason : "",
      otherKeeperLogic,
    };
    onSubmit?.(data);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      hideBackdrop={false}
      sx={{ pointerEvents: "none" }}
      ModalProps={{
        keepMounted: true,
        sx: {
          pointerEvents: "auto",
          top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
          zIndex: 1450,
        },
      }}
      PaperProps={{
        sx: {
          pointerEvents: "auto",
          width: { xs: "100%", sm: "720px", md: "720px", lg: "720px" },
          margin: { xs: "0", sm: "0 auto", md: "0 auto", lg: "0 auto" },
          height: isIOS || isAndroid
            ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
            : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"),
          border: `0.5px solid ${PureLightThemeCaaS.colors.alpha.white[70]}`,
          // border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          bgcolor: "#EDEDED",
          zIndex: 1450,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            bgcolor: "#EDEDED",
          }}
        >
          {/* Header */}
          <DialogTitle sx={{ bgcolor: "#EDEDED", borderTopLeftRadius: 5, borderTopRightRadius: 5, pb: 2, p: 1, flexShrink: 0 }}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                paddingBottom: isAndroid ? "max(env(safe-area-inset-bottom), 15px)" : "0px",
              }}
            >
              <Box sx={{ visibility: "hidden" }}>
                <IconButton aria-label="close" onClick={onClose}>
                  <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
                </IconButton>
              </Box>
              <Typography variant="h4" color={PureLightThemeCaaS.colors.alpha.black[100]} >
                Define Keeper Rules
              </Typography>
              <IconButton aria-label="close" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          {/* Divider for separation between the title and the cards */}
          <Divider
            sx={{
              margin: {
                xs: "auto",
                md: "0px",
              },
              width: "100%",
              bgcolor: "#000000",
              my: 0.5,
            }}
          />

          <Typography
            variant="h5"
            color={PureLightThemeCaaS.colors.alpha.black[100]}
            sx={{ fontSize: "15px", fontWeight: "medium", py: 1, textAlign: "center", lineHeight: 1.2, px: 1, flexShrink: 0 }}
          >
            Enter your league&apos;s specific keeper rules to create a dynamic summary for league notes.
          </Typography>

          {/* Scrollable form */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <Typography
                  variant="h5"
                  color="#000000"
                  sx={{ fontWeight: "medium" }}
                >
                  Number of Keepers
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isUnlimited}
                      onChange={(e) => handleUnlimitedChange(e.target.checked)}
                      sx={{
                        color: "#fff",
                        "&:not(.Mui-checked) .MuiSvgIcon-root": {
                          backgroundImage: "linear-gradient(#fff, #fff)",
                          backgroundSize: "14px 14px",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                        },
                        // no hover effect
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                        "&.Mui-checked": {
                          color: ORANGE,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="#000000" sx={{ fontWeight: "medium" }}>
                      Unlimited (Dynasty)
                    </Typography>
                  }
                  sx={{ mt: 0, mb: 0, ml: 0 }}
                />
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder=""
                value={numberOfKeepers}
                disabled={isUnlimited}
                onChange={(e) => handleNumericChange(e.target.value, setNumberOfKeepers)}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                sx={inputSx}
              />
            </Box>



            <Box sx={{ borderTop: "0.5px solid #000000" }}>
              <ToggleRow label="Keepers require draft pick forfeiture?" value={requiresForfeiture} onChange={handleRequiresForfeitureChange} />

              {requiresForfeiture && (
                <Box sx={{ pl: 0, }}>
                  <ToggleRow
                    label="Forfeit pick from original draft round?"
                    value={forfeitOriginalRound}
                    onChange={setForfeitOriginalRound}
                  />
                  <ToggleRow
                    label="Escalating cost for consecutive years kept?"
                    value={escalatingCost}
                    onChange={handleEscalatingCostChange}
                  />
                </Box>
              )}
            </Box>

            {requiresForfeiture && escalatingCost && (
              <Box sx={{ borderBottom: "0.5px solid #000000", pb: 2 }}>
                <Typography
                  variant="h5"
                  color="#000000"
                  sx={{ mb: 0.75, fontWeight: "medium" }}
                >
                  Round cost per season
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder=""
                  value={roundCostPerSeason}
                  onChange={(e) => handleNumericChange(e.target.value, setRoundCostPerSeason)}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  sx={inputSx}
                />
              </Box>
            )}

            <Box>
              <Typography
                variant="h5"
                color="#000000"
                sx={{ mb: 0.75, fontWeight: "medium" }}
              >
                Other Keeper logic (eg. 3 year ownership maximum)
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={otherKeeperLogic}
                onChange={(e) => setOtherKeeperLogic(e.target.value)}
                sx={{
                  ...inputSx,
                  "& .MuiOutlinedInput-root": {
                    ...inputSx["& .MuiOutlinedInput-root"],
                    alignItems: "flex-start",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Submit */}
          <Box
            sx={{
              flexShrink: 0,
              p: 1,
              pt: 2,
              pb: isIOS || isAndroid ? "max(env(safe-area-inset-bottom), 12px)" : 1,
              textAlign: "center",
            }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={!canSubmit || isSubmitting}
              onClick={handleSubmit}
              sx={{
                bgcolor: canSubmit ? ORANGE : PureLightThemeCaaS.colors.alpha.gray[30],
                color: canSubmit ? undefined : PureLightThemeCaaS.colors.alpha.black[50],
                width: "280px",
                height: "48px",
                fontSize: "1rem",
                fontWeight: "bold",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: canSubmit ? ORANGE : PureLightThemeCaaS.colors.alpha.gray[30],
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  bgcolor: PureLightThemeCaaS.colors.alpha.gray[30],
                  color: PureLightThemeCaaS.colors.alpha.black[50],
                },
              }}
            >
              {isSubmitting ? <CircularProgress size={25} sx={{ color: "#FF672D" }} /> : "Submit"}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Drawer>
  );
};

export default KeeperDrawer;
